/**
 * Get user's current location using browser geolocation API
 * @returns {Promise<{latitude: number, longitude: number}>}
 */
export const getUserLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        let errorMessage = 'Failed to get location';
        let errorType = 'unknown';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied';
            errorType = 'permission_denied';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            errorType = 'position_unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timeout';
            errorType = 'timeout';
            break;
          default:
            errorMessage = 'Unknown location error';
            errorType = 'unknown';
            break;
        }
        const err = new Error(errorMessage);
        err.errorType = errorType;
        reject(err);
      },
      {
        enableHighAccuracy: true,
        timeout: 30000, // 30 seconds - give user more time to grant permission
        maximumAge: 0,
      }
    );
  });
};

/**
 * Reverse geocode coordinates to get zipcode using Google Maps Geocoding API
 * @param {number} latitude 
 * @param {number} longitude 
 * @returns {Promise<string|null>} Returns zipcode or null if not found
 */
export const getZipcodeFromCoordinates = async (latitude, longitude) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  
  if (!apiKey) {
    console.warn('REACT_APP_GOOGLE_MAPS_API_KEY is not set');
    return null;
  }

  try {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}&result_type=postal_code`;
    
    const response = await fetch(url);
    const data = await response.json();

    if (data.status === 'OK' && data.results.length > 0) {
      // Look for postal_code in address components
      for (const result of data.results) {
        for (const component of result.address_components) {
          if (component.types.includes('postal_code')) {
            return component.long_name;
          }
        }
      }
    }

    return null;
  } catch (error) {
    console.error('Error reverse geocoding:', error);
    return null;
  }
};

/**
 * Get zipcode from user's location automatically
 * @returns {Promise<string|null>} Returns zipcode or null if failed
 * @throws {Error} If permission is denied or other critical error (with errorType property)
 */
export const getZipcodeFromLocation = async () => {
  try {
    const location = await getUserLocation();
    const zipcode = await getZipcodeFromCoordinates(location.latitude, location.longitude);
    return zipcode;
  } catch (error) {
    // Re-throw the error so caller can check errorType
    throw error;
  }
};
